/**
 * Lazy load third-party analytics scripts after page is interactive.
 * Pattern matches facebookPixel.ts - loads on idle OR first interaction.
 */

let loaded = false;

function injectGTM(): void {
  if (document.getElementById('gtm-script')) return;
  // Skip if GTM already executed from CDN (index.html direct snippet).
  // Only check the runtime object — NOT the DOM script element, because ad blockers
  // block the CDN request while leaving the <script> tag in the DOM. Checking the DOM
  // would prevent the proxy fallback from loading for ad-blocked users.
  if ((window as any).google_tag_manager?.['GTM-MMD2PC']) return;

  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.async = true;
  // Proxy URL as fallback for ad-blocked users (CDN version in index.html is primary).
  // Uses /api/p/t.js with ?c= param and stripped "GTM-" prefix to avoid ad blocker
  // pattern matching on "/gtm.js" and ".js?id=gtm-" filters.
  const containerId = (window as any).__GTM_ID__ || 'GTM-MMD2PC';
  const shortId = containerId.replace(/^GTM-/, '');
  script.src = `/api/p/t.js?c=${shortId}`;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  document.head.appendChild(script);
}

function injectMatomo(): void {
  if (document.getElementById('matomo-script')) return;

  const _paq = ((window as any)._paq = (window as any)._paq || []);
  _paq.push(['setTrackerUrl', '/api/m/e']);
  _paq.push(['setSiteId', '3']);
  _paq.push(['trackPageView']);
  _paq.push(['enableLinkTracking']);

  const script = document.createElement('script');
  script.id = 'matomo-script';
  script.async = true;
  script.src = '/api/m/t.js';
  document.head.appendChild(script);
}

function injectAdRoll(): void {
  if (document.getElementById('adroll-script')) return;

  (window as any).adroll_adv_id = 'ADW5RYFUGRADPN34344HEI';
  (window as any).adroll_pix_id = 'CLDREFE7CJGCJL3RL7PJHL';
  (window as any).adroll_version = '2.0';
  (window as any).__adroll_loaded = true;
  (window as any).__adroll = (window as any).__adroll || [];

  const script = document.createElement('script');
  script.id = 'adroll-script';
  script.async = true;
  script.src = '/api/ar/j/roundtrip.js';
  document.head.appendChild(script);
}

function loadAllAnalytics(): void {
  if (loaded) return;
  loaded = true;

  injectGTM();
  injectMatomo();
  injectAdRoll();

  // eslint-disable-next-line no-console
  console.debug('[Analytics] Third-party scripts loaded (deferred)');
}

export function initDeferredAnalytics(options?: { idleTimeoutMs?: number }): void {
  if (typeof window === 'undefined') return;

  const idleTimeoutMs = options?.idleTimeoutMs ?? 5000;

  const trigger = () => loadAllAnalytics();

  // Load immediately for paid traffic (gclid/gbraid/wbraid in URL)
  if (window.location.search.match(/[?&](gclid|gbraid|wbraid)=/)) {
    trigger();
    return;
  }

  // Load on idle or after timeout
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(trigger, { timeout: idleTimeoutMs });
  } else {
    setTimeout(trigger, idleTimeoutMs);
  }

  // Also load on first interaction
  const onInteract = () => {
    trigger();
    window.removeEventListener('scroll', onInteract);
    window.removeEventListener('click', onInteract);
  };

  window.addEventListener('scroll', onInteract, { passive: true, once: true });
  window.addEventListener('click', onInteract, { passive: true, once: true });
}
